// Adapter layer: shapes assessmentResult + insightfulContent (both real
// API responses) into the single normalized `reportData` view-model
// component.js renders. Real, mock, and DevTools-scenario-injected input
// all flow through this same function.
//
// Two independent, additive scoring systems live in `overallResult`:
//   - `percentage`/`bandKey`/`classificationLabel` — the confirmed 5-band
//     PERFORMANCE system, computed from `percentage` via iq-calculations.js.
//     Sole authority for section classification and ALL_CONTENT narrative
//     selection (overview/strength/improve).
//   - `psychometric` — the fixed Mean=40.5/SD=7.8 IQ/deviation index
//     (computeFixedNormIq, iq-psychometrics.js), computed from the SAME
//     overallRawScore. Never overwrites percentage/bandKey. No per-domain
//     IQ exists (team-confirmed: overall 60-item score only), and no
//     confidenceInterval/reliability/SEM/scaledScore (no confirmed
//     methodology for those under this model).
// The two systems use overlapping-looking vocabularies on different
// scales (see PSYCHOMETRIC_CLASSIFICATION_BANDS vs.
// OFFICIAL_PERFORMANCE_BANDS) — never conflate them.
//
// `consistency` is a third, separate additive metric (spread across the
// four section percentages) — see deriveConsistency's own comment.
//
// Contract (2026-09-17): each domain's raw score comes directly from the
// backend's own `userTrait.score` (an authoritative correct-answer count)
// — never reconstructed from `userTrait.percentage`. `percentage` stays
// completely independent, feeding only display/bandKey/consistency below.

import { QUESTIONS_PER_DOMAIN, TOTAL_QUESTIONS } from "./iq-assessment-config";
import {
  deriveOverallRawScore,
  deriveOverallPercentage,
  deriveBandKeyFromPercentage,
  deriveConsistency,
} from "./iq-calculations";
import { IQ_SCORING_CONFIG } from "./iq-psychometric-config";
import { computeFixedNormIq } from "./iq-psychometrics";

// The real result's trait key ("visual_pattern_recognition") doesn't
// match the content model's section key ("pattern_recognition") — a
// confirmed backend/content inconsistency. Bridged for content lookup
// only; the normalized `key` below is the canonical form everything else
// (styling, illustrations, DevTools) keys off.
const RESULT_TO_CONTENT_DOMAIN_KEY = {
  visual_pattern_recognition: "pattern_recognition",
};

// Exported so dev-tools/report-debug-panel.js keys its raw-input
// injection by the same canonical domain key, never a second copy.
export const canonicalizeDomainKey = (rawKey) => RESULT_TO_CONTENT_DOMAIN_KEY[rawKey] || rawKey;

// Fixed display order — the real backend does not return userTraits in
// this order.
const CANONICAL_DOMAIN_ORDER = [
  "pattern_recognition",
  "verbal_reasoning",
  "numerical_reasoning",
  "logical_deduction",
];

// getInsightfulContent resolves to { content: { Content: {...} } } — the
// displayable fields live two levels down. Tolerates a flatter shape too.
export const unwrapIQContent = (insightfulContent) => {
  const outer = insightfulContent?.content ?? insightfulContent;
  return outer?.Content ?? outer?.content ?? outer;
};

export const mapIQReportModel = (assessmentResult, insightfulContent) => {
  const content = unwrapIQContent(insightfulContent) || {};
  const detailedBreakdown = content.detailedBreakdown;

  const domains = (assessmentResult.userTraits || [])
    .map((userTrait) => {
      const canonicalKey = canonicalizeDomainKey(userTrait.trait?.title);
      const section = detailedBreakdown?.sections?.[canonicalKey];
      const percentage = userTrait.percentage;
      const domainBandKey = deriveBandKeyFromPercentage(percentage);
      const bandCopy = domainBandKey ? section?.bands?.[domainBandKey] : null;
      // Authoritative backend raw correct-answer count for this section —
      // read directly, never reconstructed from percentage.
      const rawScore = userTrait.score != null ? Number(userTrait.score) : null;

      return {
        key: canonicalKey,
        title: section?.label ?? null,
        subtitle: section?.subtitle ?? null,
        rawScore,
        maxRawScore: QUESTIONS_PER_DOMAIN,
        percentage,
        // No per-domain IQ/psychometric field — the fixed-norm model
        // applies to the overall 60-item score only.
        bandKey: domainBandKey,
        overview: bandCopy?.overview ?? null,
        strength: bandCopy?.strength ?? null,
        improve: bandCopy?.howToImprove ?? null,
        classificationLabel: domainBandKey
          ? content.overallResult?.bands?.[domainBandKey]?.label ?? null
          : null,
      };
    })
    .sort(
      (a, b) => CANONICAL_DOMAIN_ORDER.indexOf(a.key) - CANONICAL_DOMAIN_ORDER.indexOf(b.key)
    );

  const overallRawScore = deriveOverallRawScore(domains.map((domain) => domain.rawScore));
  const overallMaxRawScore = overallRawScore != null ? TOTAL_QUESTIONS : null;
  const overallPercentage = deriveOverallPercentage(overallRawScore, TOTAL_QUESTIONS);
  const overallBandKey = deriveBandKeyFromPercentage(overallPercentage);
  const overallBandContent = overallBandKey ? content.overallResult?.bands?.[overallBandKey] : null;
  const overallConsistency = deriveConsistency(domains.map((domain) => domain.percentage));

  return {
    header: { title: content.header ?? null },
    intro: { body: content.intro ?? null },
    overallResult: {
      rawScore: overallRawScore,
      maxRawScore: overallMaxRawScore,
      percentage: overallPercentage,
      psychometric: computeFixedNormIq(overallRawScore, IQ_SCORING_CONFIG),
      consistency: overallConsistency,
      bandKey: overallBandKey,
      classificationLabel: overallBandContent?.label ?? null,
      classificationDescription: overallBandContent?.description ?? null,
      heading: content.overallResult?.heading ?? null,
    },
    domains,
    detailedBreakdownIntro: {
      heading: detailedBreakdown?.heading ?? null,
      body: detailedBreakdown?.intro ?? null,
      // Localized Deep Dive card labels — content-owned, rendered as-is;
      // component.js supplies a t() fallback when absent.
      strengthLabel: detailedBreakdown?.subheadings?.strength ?? null,
      improveLabel: detailedBreakdown?.subheadings?.howToImprove ?? null,
    },
  };
};
